import React from "react";
import { RecrystaMark } from "./RecrystaLogo";

/**
 * Animated inbox mock for the landing hero.
 * Pure CSS keyframes — no video, no dependencies.
 *
 * Loop (7s):
 *   0.0s — inbox shown, Recrysta row collapsed
 *   0.5s — Recrysta row fades/slides in
 *   1.8s — cursor pointer enters from bottom-right
 *   2.8s — cursor reaches the Recrysta row
 *   3.0s — click ripple, row expands to reveal briefing peek
 *   5.5s — peek fades out, cursor leaves
 *   7.0s — restart
 */
const otherRows = [
  { from: "Marcus Feld", subject: "Re: contract signed 🎉", time: "07:14" },
  { from: "Priya Shah", subject: "Quick question about pricing", time: "07:02" },
  { from: "Calendar", subject: "10:00 — Pipeline review with sales", time: "06:41" },
  { from: "Weekly digest", subject: "5 metrics changed this week", time: "06:28" },
];

export const InboxMock = () => {
  return (
    <div className="relative w-full max-w-md mx-auto recrysta-mock-reserve" data-testid="landing-inbox-mock">
      {/* Ambient emerald glow */}
      <div className="absolute -inset-8 bg-emerald-500/10 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />

      <div className="relative rounded-2xl bg-white ring-1 ring-slate-200 shadow-2xl overflow-hidden recrysta-mock-float">
        {/* Window chrome */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
          <div className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
          <div className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
          <div className="ml-3 text-[10px] font-mono text-slate-400 tracking-wide">inbox — mon 06:02</div>
        </div>

        {/* Inbox rows */}
        <div className="relative">
          {/* Recrysta briefing */}
          <div
            className="relative flex items-start gap-3 px-4 py-3.5 border-b border-slate-100 bg-emerald-50/60 recrysta-row-anim"
            data-testid="landing-inbox-recrysta-row"
          >
            <div className="mt-0.5 shrink-0">
              <RecrystaMark size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[13px] font-bold text-slate-900 truncate">
                  Recrysta
                  <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 recrysta-pulse" />
                    new
                  </span>
                </div>
                <div className="text-[10px] font-mono text-slate-400 shrink-0">06:02</div>
              </div>
              <div className="mt-0.5 text-[12px] text-slate-700 truncate">
                Monday Briefing — Q3 forecast <span className="text-red-600 font-semibold">down €20,750</span>. 2 deals need action.
              </div>
              <div className="mt-2 text-[11px] text-slate-500 leading-snug">
                Weighted forecast €138,391, tracking 46% of €300,000 target — gap of €41,609…
              </div>
            </div>
          </div>

          {/* Expanded peek — reveals as an OVERLAY on top of competing rows
              (position:absolute so opening/closing never reflows layout) */}
          <div className="recrysta-peek absolute left-0 right-0 z-10 bg-white/95 backdrop-blur-sm border-b border-emerald-100" style={{ top: "92px" }}>
            <div className="px-4 pb-4 pt-2">
              <div className="mt-1 rounded-md border border-emerald-200 bg-white p-3 shadow-sm">
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-2">
                  /forecast_delta
                </div>
                <div className="flex items-baseline gap-2 mb-2.5">
                  <div className="text-lg font-bold text-slate-900 number-tabular">€138,391</div>
                  <div className="text-[10px] text-slate-500">weighted · gap €41,609 to €300k target</div>
                </div>
                <div className="space-y-1.5">
                  <PeekDeal color="bg-red-500" name="Quantum Retail — Expansion" note="Proposal · €26,500" />
                  <PeekDeal color="bg-amber-500" name="Aurora Health — SaaS renewal" note="Negotiation · €18,200" />
                </div>
              </div>
            </div>
          </div>

          {/* Muted competing emails */}
          {otherRows.map((r, i) => (
            <div
              key={r.subject}
              className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 recrysta-fade-in"
              style={{ animationDelay: `${300 + i * 90}ms` }}
            >
              <div className="mt-1 h-5 w-5 shrink-0 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-[10px] font-semibold">
                {r.from.charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[12px] font-semibold text-slate-500 truncate">{r.from}</div>
                  <div className="text-[10px] font-mono text-slate-400 shrink-0">{r.time}</div>
                </div>
                <div className="text-[11px] text-slate-400 truncate">{r.subject}</div>
              </div>
            </div>
          ))}

          {/* Cursor pointer — animates in, moves to Recrysta row, clicks */}
          <div className="recrysta-cursor" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M4 2l14 8-5.5 1.5L15 18l-2 1-2.5-6.5L4 14V2z"
                fill="#0f172a"
                stroke="#ffffff"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
            <span className="recrysta-cursor-ripple" />
          </div>
        </div>
      </div>

      {/* Caption */}
      <div className="mt-4 text-center text-[11px] font-mono uppercase tracking-widest text-slate-400">
        &nbsp;·&nbsp; delivered Monday 06:00 &nbsp;·&nbsp;
      </div>

      <style>{`
        /* Reserve space so the peek expand/collapse never reflows the hero */
        .recrysta-mock-reserve { min-height: 470px; }
        @media (max-width: 640px) {
          .recrysta-mock-reserve { min-height: 440px; }
        }

        /* Gentle float applied ONLY to the mockup card — transform-only, no layout impact */
        @keyframes recrystaFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        .recrysta-mock-float {
          animation: recrystaFloat 6s ease-in-out infinite;
          will-change: transform;
        }

        /* Recrysta row: fade + slide in early each loop */
        @keyframes recrystaRowIn {
          0%   { opacity: 0; transform: translateY(-10px); }
          8%   { opacity: 1; transform: translateY(0); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .recrysta-row-anim {
          animation: recrystaRowIn 7s ease-out infinite;
        }

        /* Green NEW dot pulse */
        @keyframes recrystaPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.7); opacity: 0.4; }
        }
        .recrysta-pulse {
          animation: recrystaPulse 1.6s ease-in-out infinite;
        }

        /* Other rows: single fade-in on mount */
        @keyframes recrystaFadeInOnce {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .recrysta-fade-in {
          opacity: 0;
          animation: recrystaFadeInOnce 500ms ease-out forwards;
        }

        /* Cursor path: bottom-right corner → over Recrysta row → click → back */
        .recrysta-cursor {
          position: absolute;
          right: 20px;
          bottom: 20px;
          width: 20px;
          height: 20px;
          opacity: 0;
          pointer-events: none;
          filter: drop-shadow(0 1px 2px rgba(0,0,0,0.15));
          animation: recrystaCursorMove 7s ease-in-out infinite;
        }
        @keyframes recrystaCursorMove {
          0%    { opacity: 0; transform: translate(0, 0); }
          18%   { opacity: 0; transform: translate(0, 0); }
          24%   { opacity: 1; transform: translate(0, 0); }
          40%   { opacity: 1; transform: translate(-140px, -240px); }
          43%   { opacity: 1; transform: translate(-140px, -240px) scale(0.9); }
          46%   { opacity: 1; transform: translate(-140px, -240px) scale(1); }
          78%   { opacity: 1; transform: translate(-140px, -240px); }
          88%   { opacity: 0; transform: translate(-140px, -240px); }
          100%  { opacity: 0; transform: translate(0, 0); }
        }

        /* Click ripple emanating from the cursor at click moment */
        .recrysta-cursor-ripple {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 8px;
          height: 8px;
          border-radius: 9999px;
          background: rgba(16, 185, 129, 0.35);
          transform: translate(-50%, -50%) scale(0);
          animation: recrystaClickRipple 7s ease-out infinite;
        }
        @keyframes recrystaClickRipple {
          0%, 42%   { transform: translate(-50%, -50%) scale(0); opacity: 0; }
          43%       { transform: translate(-50%, -50%) scale(0); opacity: 0.9; }
          52%       { transform: translate(-50%, -50%) scale(6); opacity: 0; }
          100%      { transform: translate(-50%, -50%) scale(0); opacity: 0; }
        }

        /* Peek: overlay-reveals right after the click, holds, then fades out.
           Uses opacity + translateY only — never changes layout height. */
        .recrysta-peek {
          opacity: 0;
          transform: translateY(-8px);
          pointer-events: none;
          animation: recrystaPeekReveal 7s ease-in-out infinite;
        }
        @keyframes recrystaPeekReveal {
          0%, 44%   { opacity: 0; transform: translateY(-8px); }
          50%       { opacity: 1; transform: translateY(0); }
          78%       { opacity: 1; transform: translateY(0); }
          84%       { opacity: 0; transform: translateY(-8px); }
          100%      { opacity: 0; transform: translateY(-8px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .recrysta-mock-float,
          .recrysta-row-anim,
          .recrysta-pulse,
          .recrysta-fade-in,
          .recrysta-cursor,
          .recrysta-cursor-ripple,
          .recrysta-peek {
            animation: none !important;
            opacity: 1 !important;
            max-height: none !important;
          }
        }
      `}</style>
    </div>
  );
};

const PeekDeal = ({ color, name, note }) => (
  <div className="flex items-center gap-2 text-[11px]">
    <span className={`h-1.5 w-1.5 rounded-full ${color} shrink-0`} />
    <span className="font-semibold text-slate-900">{name}</span>
    <span className="text-slate-500 truncate">· {note}</span>
  </div>
);

export default InboxMock;
